// src/components/PromoFormCrystals.jsx

import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCrystals, setSelectedId } from '../../../store/slices/crystalsSlice';
import { setPage, goBack } from '../../../store/slices/pageSlice';
import { usePromoLeaveGuard } from '../../../context/PromoLeaveGuardContext';
import { clearPromoCreationState } from '../../../utils/promoFormDraft';
import { clearEditMode } from '../../../store/slices/selectedPromoSlice';
import { setPromoterData } from '../../../store/slices/promoTelegramSlice';
import { setLink as setFormLink, setParticipantsCount, setPrizePlacesCount, setMaxParticipantsCount } from '../../../store/slices/promoFormSlice';
import { getCrystalCategoryPlayers, getCrystalCategoryBudget, findCrystalCategoryForPromo } from '../../../utils/participantLimits';
import { buildCrystalPositions } from '../../../utils/crystalPositions';
import { setTasks, trimTasksToMax } from '../../../store/slices/TasksSlice';
import PromosApi from '../../../api/PromosApi';
import TaskApi from '../../../api/taskApi';
import bonusApi from '../../../api/bonusApi';

import PromoForm from '../PromoForm';
import FormAccardion from '../items/FormAccardion';
import CoinBadge from '../../coin_badge/CoinBadge';
import TasksList from '../items/TasksList';
import Header from '../../header/Header';
import { PromoTypeIntroBanner } from '../PromoGuideContext';

const API_BASE_URL = 'https://api.duotetra.quest';

const Snackbar = React.memo(function SnackbarInner({ message, type, isVisible, onClose, isAnimated, tokenData, blockchainName, isBlockchainSwitched }) {
    if (!isVisible) return null;

    const bgColor = type === 'hint'
        ? 'bg-[#232323]'
        : type === 'error'
            ? 'bg-[#fb2c36]'
            : 'bg-[#00c950]';
    const borderColor = type === 'hint'
        ? 'border-transparent'
        : type === 'error'
            ? 'border-red-500'
            : 'border-green-500';

    return (
        <div className={`fixed top-0 left-1/2 transform -translate-x-1/2 z-50 
            ${bgColor} ${borderColor} border text-white px-4 pt-24 pb-3 shadow-xl 
            transition-all duration-500 ease-out min-w-[100%]
            ${isAnimated ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-full opacity-0'}
        `}>
            <div className="flex items-center justify-between">
                <div className="flex items-center justify-center space-x-2 flex-1">
                    {type !== 'hint' && (
                        <div className={`w-1.5 h-1.5 rounded-full ${type === 'error' ? 'bg-red-300' : 'bg-green-300'}`} />
                    )}

                    {type === 'success' && tokenData ? (
                        <div className="flex items-center space-x-2">
                            <img
                                src={`${API_BASE_URL}/${tokenData.logoPath}`}
                                alt={tokenData.symbol}
                                className="w-5 h-5 rounded-full object-cover"
                            />
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-white">{message}</span>
                                <div className="flex items-center space-x-1 mt-0.5">
                                    <span className="text-[10px] text-gray-300">{tokenData.symbol}</span>
                                    <span className="text-[10px] text-gray-400">•</span>
                                    <span className="text-[10px] text-gray-300">{blockchainName}</span>
                                    {isBlockchainSwitched && (
                                        <>
                                            <span className="text-[10px] text-gray-400">•</span>
                                            <span className="text-[10px] text-yellow-300">🔄 Авто</span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <span className="text-xs font-medium text-white text-center">{message}</span>
                    )}
                </div>

                <button
                    onClick={onClose}
                    className="text-white/70 hover:text-white transition-colors duration-200 flex-shrink-0 ml-3"
                    aria-label="Close notification"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {type !== 'hint' && (
                <div className="absolute bottom-0 left-0 h-0.5 bg-white/20 overflow-hidden w-full">
                    <div className={`h-full ${type === 'error' ? 'bg-red-300' : 'bg-green-300'} transition-all duration-4000 ease-linear`} />
                </div>
            )}
        </div>
    );
});

const CRYSTAL_CATEGORY_MAX_TASKS = {
    3: 3,
    5: 4,
    7: 5,
};

const getCrystalCategoryMaxTasks = (categoryNumber) =>
    CRYSTAL_CATEGORY_MAX_TASKS[categoryNumber] ?? 5;

export default function PromoFormCrystals() {
    const dispatch = useDispatch();
    const { requestLeave } = usePromoLeaveGuard();
    const { items: crystals, icons, selectedId, loading, error } = useSelector(s => s.crystals);
    const { link, data } = useSelector((s) => s.promoTelegram);
    const tasks = useSelector((s) => s.tasks?.items || []);
    const tasksLoaded = useSelector((s) => s.tasks?.tasksLoaded || false);
    const tasksModified = useSelector((s) => s.tasks?.tasksModified || false);
    const tasksCount = tasks.length;
    const selectedCategory = crystals.find(cat => cat.id === selectedId);
    const maxTasks = getCrystalCategoryMaxTasks(selectedCategory?.number);
    const customParticipants = useSelector(s => s.promoForm.participantsCount);
    const customWinners = useSelector(s => s.promoForm.prizePlacesCount);
    const hydratedPromoIdRef = useRef(null);
    const type = 'crystals';

    // If category max tasks shrinks (e.g. 7→3 cat), drop overflow tasks so count never exceeds limit (5/3 bug)
    useEffect(() => {
        if (tasksCount <= maxTasks) return;
        dispatch(trimTasksToMax(maxTasks));
    }, [maxTasks, tasksCount, dispatch]);
    
    // Edit mode
    const { promo: editPromo, isEditMode, editMode } = useSelector(s => s.selectedPromo);
    const disabledDefaultTaskIds = useSelector((s) => s.tasks?.disabledDefaultTaskIds || []);
    const isLimited = editMode === 'limited';
    
    useEffect(() => {
        if (loading) hydratedPromoIdRef.current = null;
    }, [loading]);

    useEffect(() => {
        if (!isEditMode) {
            hydratedPromoIdRef.current = null;
            return;
        }
        // Wait for this form's fetchCrystals — stale list from another type left
        // category/players empty after selectedId was cleared on fulfill.
        if (!editPromo?.id || loading || crystals.length === 0) return;

        const crystalsKey = crystals.map((c) => c.id).join(',');
        const hydrateKey = `${editPromo.id}:${crystalsKey}`;
        if (hydratedPromoIdRef.current === hydrateKey) return;
        hydratedPromoIdRef.current = hydrateKey;

        const matchingCategory = findCrystalCategoryForPromo(crystals, editPromo);
        const participants = editPromo.participantsCount;
        const winners = editPromo.prizePlacesCount;
        const max = editPromo.maxParticipantsCount || participants;

        if (participants) dispatch(setParticipantsCount(participants));
        if (winners) dispatch(setPrizePlacesCount(winners));
        if (max) dispatch(setMaxParticipantsCount(max));

        if (matchingCategory) {
            dispatch(setSelectedId(matchingCategory.id));
        }
    }, [isEditMode, editPromo, crystals, loading, dispatch]);

    useEffect(() => {
        if (!selectedCategory || isEditMode) return;
        const players = getCrystalCategoryPlayers(selectedCategory);
        dispatch(setParticipantsCount(players));
        dispatch(setPrizePlacesCount(players));
        dispatch(setMaxParticipantsCount(players));
    }, [selectedCategory?.id, isEditMode, dispatch, selectedCategory]);

    // Bonus position (1st Time Free)
    const bonusPosition = useSelector(s => s.promoForm?.bonusPosition);
    const isBonusMode = !!bonusPosition;

    // Состояния для snackbar
    const [snackbar, setSnackbar] = React.useState({ isVisible: false, message: '', type: 'error', tokenData: null, blockchainName: '', isBlockchainSwitched: false });
    const [snackbarAnimation, setSnackbarAnimation] = React.useState(false);

    // Функции для работы с snackbar
    const closeSnackbar = useCallback(() => {
        setSnackbarAnimation(false);
        setTimeout(() => {
            setSnackbar(prev => ({ ...prev, isVisible: false }));
        }, 300);
    }, []);

    const showSnackbar = useCallback((message, type, tokenData = null, blockchainName = '', isBlockchainSwitched = false) => {
        setSnackbar({ isVisible: true, message, type, tokenData, blockchainName, isBlockchainSwitched });
        setTimeout(() => setSnackbarAnimation(true), 100);
    }, []);

    // Автоматическое закрытие snackbar через 4 секунды
    React.useEffect(() => {
        if (snackbar.isVisible) {
            const timer = setTimeout(closeSnackbar, 4000);
            return () => clearTimeout(timer);
        }
    }, [snackbar.isVisible, closeSnackbar]);

    // Проверяем, заполнена ли форма для показа кнопки FINISH
    const isFormComplete = useMemo(() => {
        return (
            selectedId &&                           // Категория кристаллов выбрана
            link && link.trim() !== '' &&           // Ссылка на промоутера заполнена
            link !== 'Promoter link' && // Не дефолтное значение
            data && data.name                       // Данные промоутера получены
        );
    }, [selectedId, link, data]);

    const canSubmitPromo = isFormComplete && tasksCount > 0;

    const createNewPromo = async () => {
        try {
            // Проверка обязательных полей
            if (!selectedCategory) {
                showSnackbar('Выберите категорию кристаллов', 'error');
                return;
            }
            if (!link || link.trim() === '' || link === 'Promoter link') {
                showSnackbar('Введите ссылку на Telegram канал', 'error');
                return;
            }
            if (!data || !data.name) {
                showSnackbar('Загрузите данные промоутера (нажмите Enter в поле ссылки)', 'error');
                return;
            }

            // Для кристаллов бюджет берётся из defaultBudget категории
            // defaultBudget приходит как string из decimal поля PostgreSQL
            const totalBudget = getCrystalCategoryBudget(selectedCategory);
            const categoryPlayers = getCrystalCategoryPlayers(selectedCategory);
            const effectiveParticipants = isEditMode
                ? (customParticipants || editPromo?.participantsCount || categoryPlayers)
                : categoryPlayers;
            const effectiveWinners = isEditMode
                ? (customWinners || editPromo?.prizePlacesCount || categoryPlayers)
                : categoryPlayers;
            const totalPlaces = effectiveWinners;
            
            console.log('[PromoFormCrystals] totalBudget:', totalBudget, 'raw:', selectedCategory?.defaultBudget);
            console.log('[PromoFormCrystals] totalPlaces:', totalPlaces);
            
            const positions = buildCrystalPositions(
                selectedCategory?.presets,
                totalBudget,
                totalPlaces
            );

            if (positions.length === 0) {
                showSnackbar('Категория не содержит позиций', 'error');
                return;
            }

            // В бонусном режиме используем bonusApi
            if (isBonusMode) {
                console.log('[Bonus Mode] Creating bonus promo...');
                
                // Получаем задачи из Redux
                const currentTasks = tasks || [];
                
                const bonusPromoData = {
                    categoryId: bonusPosition.categoryId,
                    participantsCount: effectiveParticipants,
                    prizePlacesCount: effectiveWinners,
                    link: link.trim(),
                    promoters: data ? [data] : [],
                    durationDays: selectedCategory?.number ?? 7,
                    tasks: currentTasks.map(t => ({
                        title: t.text,
                        details: t.link || '',
                        type: t.label || 'default',
                        metadata: t.metadata || {},
                    })),
                    category: {
                        category_name: selectedCategory?.name || "Unknown",
                        positions: positions,
                    },
                };
                
                console.log('[Bonus Mode] Sending data:', bonusPromoData);
                
                const response = await bonusApi.createBonusPromo(bonusPromoData);
                console.log('[Bonus Mode] Promo created:', response);

                showSnackbar('Бонусное промо успешно создано!', 'success');
                
                // Очищаем bonusPosition после успешного создания
                dispatch({ type: 'promoForm/setBonusPosition', payload: null });
                forceExitPromoForm();
                return;
            }

            // Обычное создание промо
            const promoData = {
                title: `Promo ${Date.now()}`,
                participantsCount: effectiveParticipants,
                prizePlacesCount: effectiveWinners,
                totalAmount: totalBudget, // Бюджет из defaultBudget категории
                currentAmount: 0,
                status: "PENDING",
                winnersCount: 0,
                durationDays: selectedCategory?.number ?? 7,
                tokenType: "", // Пустая строка вместо null
                creatorUserId: 1,
                link: link.trim(),
                maxParticipantsCount: effectiveParticipants,
                category: {
                    category_name: selectedCategory?.name || "Unknown",
                    positions: positions,
                },
                promoType: "onlyCrystals", // Правильное значение enum
                promoters: data ? [data] : [],
                selectedCrystalCategory: selectedCategory,
                promoTime: selectedCategory?.number ?? 7,
                tasks: [],
                disabledDefaultTaskIds
            };
            console.log('Отправка данных промо:', promoData);
            
            const response = await PromosApi.create(promoData);
            console.log('Промо создано:', response);

            // Создаём задачи для нового промо (если есть)
            const createdPromoId = response?.id;
            if (createdPromoId && tasks.length > 0) {
                try {
                    const tasksForApi = tasks.map(task => ({
                        title: task.text,
                        details: task.link,
                    }));
                    await TaskApi.createTasksForPromo(createdPromoId, tasksForApi);
                    console.log(`Задачи (${tasksForApi.length}) созданы для промо ${createdPromoId}`);
                } catch (taskErr) {
                    console.error('Ошибка создания задач:', taskErr);
                    showSnackbar('Промо создано, но задачи не сохранены. Можно добавить через редактирование.', 'error');
                    forceExitPromoForm();
                    return;
                }
            }

            showSnackbar('Promo saved', 'success');
            setTimeout(() => {
                forceExitPromoForm();
            }, 1200);

        } catch (error) {
            console.error('Ошибка создания промо:', error);
            const errorMessage = error.response?.data?.message 
                || error.response?.data?.error 
                || error.message 
                || 'Ошибка создания промо';
            console.error('Детали ошибки:', error.response?.data);
            showSnackbar(errorMessage, 'error');
        }
    };

    const handleUpdatePromo = async () => {
        if (!editPromo?.id) {
            showSnackbar('Нет данных промо для обновления', 'error');
            return;
        }
        
        try {
            // В limited режиме можно менять только tasks
            // Tasks обрабатываются отдельно через TaskApi в TasksList
            
            if (!isLimited) {
                // Full mode - можно менять всё
                // Для кристаллов бюджет берётся из defaultBudget категории
                const totalBudget = getCrystalCategoryBudget(selectedCategory);
                const categoryPlayers = getCrystalCategoryPlayers(selectedCategory);
                const totalPlaces = customWinners || editPromo.prizePlacesCount || categoryPlayers;

                const positions = buildCrystalPositions(
                    selectedCategory?.presets,
                    totalBudget,
                    totalPlaces
                );

                const editEffectiveParticipants = customParticipants || editPromo.participantsCount || categoryPlayers;
                
                const updateData = {
                    participantsCount: editEffectiveParticipants,
                    prizePlacesCount: totalPlaces,
                    maxParticipantsCount: editEffectiveParticipants,
                    totalAmount: totalBudget,
                    durationDays: editPromo.durationDays || 7,
                    link: link?.trim() || editPromo.link,
                    category: {
                        category_name: selectedCategory?.name || editPromo.category?.category_name,
                        positions: positions.length > 0 ? positions : editPromo.category?.positions,
                    },
                    promoters: data ? [data] : editPromo.promoters,
                };
                
                await PromosApi.update(editPromo.id, updateData);
                
                // Сохраняем задачи только если они были загружены или изменены
                if (tasksLoaded || tasksModified) {
                    const tasksForApi = tasks.map(task => ({
                        title: task.text,
                        details: task.link,
                    }));
                    await TaskApi.createTasksForPromo(editPromo.id, tasksForApi);
                }
            } else {
                // Limited mode - можно менять только tasks
                if (tasksLoaded || tasksModified) {
                    const tasksForApi = tasks.map(task => ({
                        title: task.text,
                        details: task.link,
                    }));
                    await TaskApi.createTasksForPromo(editPromo.id, tasksForApi);
                }
            }
            
            showSnackbar('Promo saved', 'success');
            setTimeout(() => {
                forceExitPromoForm();
            }, 1200);
            
        } catch (error) {
            console.error('Ошибка обновления промо:', error);
            const errorMessage = error.response?.data?.message 
                || error.response?.data?.error 
                || error.message 
                || 'Ошибка обновления промо';
            showSnackbar(errorMessage, 'error');
        }
    };

    const hasPromoter = Boolean(
        link && link.trim() !== '' && link !== 'Promoter link' && data?.name
    );

    const handleFinishClick = useCallback(() => {
        if (canSubmitPromo) {
            if (isEditMode) handleUpdatePromo();
            else createNewPromo();
            return;
        }

        const parts = [];
        if (!hasPromoter) parts.push('· Add a Promoter');
        if (tasksCount <= 0) parts.push('· Add at least one Task');
        if (parts.length === 0) {
            if (!selectedId) parts.push('· Select reward categories');
        }

        showSnackbar(parts.join('  ') || '· Complete all fields', 'hint');
    }, [
        canSubmitPromo,
        isEditMode,
        handleUpdatePromo,
        createNewPromo,
        hasPromoter,
        tasksCount,
        selectedId,
        showSnackbar,
    ]);

    const forceExitPromoForm = () => {
        dispatch(clearEditMode());
        clearPromoCreationState(dispatch);
        dispatch(goBack());
    };

    const handleClose = () => {
        requestLeave(forceExitPromoForm);
    };

    useEffect(() => {
        // Используем promoType из bonusPosition если есть, иначе дефолт
        const promoType = bonusPosition?.promoType || 'onlyCrystals';
        dispatch(fetchCrystals(promoType));
    }, [dispatch, bonusPosition?.promoType]);

    // Загрузка данных промоутера при редактировании
    useEffect(() => {
        if (!isEditMode || !editPromo) return;
        
        const BASE_URL = 'https://api.duotetra.quest';
        
        // Устанавливаем ссылку в promoFormSlice
        if (editPromo.link) {
            dispatch(setFormLink(editPromo.link));
        }
        
        // Устанавливаем данные промоутера из промо
        const promoter = editPromo.promoters?.[0];
        if (promoter) {
            const logoPath = promoter.logo || promoter.logoPath || '';
            const logoUrl = logoPath.startsWith('http') 
                ? logoPath 
                : logoPath.startsWith('/') 
                    ? `${BASE_URL}${logoPath}` 
                    : logoPath ? `${BASE_URL}/${logoPath}` : '';
            
            dispatch(setPromoterData({
                name: promoter.name || promoter.title,
                logo: logoUrl,
                link: editPromo.link,
            }));
        } else if (editPromo.link) {
            // Если нет данных промоутера но есть ссылка - устанавливаем хотя бы ссылку
            dispatch(setPromoterData({
                name: null,
                logo: null,
                link: editPromo.link,
            }));
        }
    }, [isEditMode, editPromo, dispatch]);

    // Загрузка задач при редактировании
    useEffect(() => {
        if (!isEditMode || !editPromo?.id) return;
        
        const loadTasks = async () => {
            try {
                const tasksFromApi = await TaskApi.getTasksByPromo(editPromo.id);
                if (tasksFromApi && tasksFromApi.length > 0) {
                    const formattedTasks = tasksFromApi.map(task => ({
                        text: task.title,
                        label: task.details?.includes('telegram') ? 'telegram' : 
                               task.details?.includes('twitter') || task.details?.includes('x.com') ? 'twitter' : 'link',
                        link: task.details || '',
                        isComplated: false,
                    }));
                    dispatch(setTasks(formattedTasks));
                } else {
                    dispatch(setTasks([]));
                }
            } catch (error) {
                console.error('Ошибка загрузки задач:', error);
            }
        };
        
        loadTasks();
    }, [isEditMode, editPromo?.id, dispatch]);

    // Сброс selectedId при входе в бонусный режим
    useEffect(() => {
        if (isBonusMode && bonusPosition?.categoryId) {
            // Сбрасываем чтобы потом выбрать правильную категорию
            dispatch(setSelectedId(null));
        }
    }, [isBonusMode, bonusPosition?.categoryId, dispatch]);

    useEffect(() => {
        if (!loading && !error && crystals.length > 0 && !isEditMode) {
            // Если есть bonusPosition — ВСЕГДА выбираем указанную категорию
            if (bonusPosition?.categoryId) {
                const bonusCategoryId = Number(bonusPosition.categoryId);
                const bonusCategory = crystals.find(c => Number(c.id) === bonusCategoryId);
                console.log('[PromoFormCrystals] Bonus mode: looking for categoryId', bonusCategoryId, 'in crystals', crystals.map(c => ({ id: c.id, name: c.name })));
                if (bonusCategory && selectedId !== bonusCategory.id) {
                    console.log('[PromoFormCrystals] Found bonus category:', bonusCategory.name);
                    dispatch(setSelectedId(bonusCategory.id));
                    return;
                }
            }
            // По умолчанию — последняя категория (только если не в бонусном режиме и нет selectedId)
            if (selectedId == null && !isBonusMode) {
                dispatch(setSelectedId(crystals[crystals.length - 1]?.id || crystals[0]?.id));
            }
        }
    }, [loading, error, crystals, selectedId, dispatch, isEditMode, bonusPosition, isBonusMode]);

    const Categories = () => {
        if (loading) return <p className="text-white text-[12px]">Загрузка...</p>;
        if (error)   return <p className="text-red-500 text-[12px]">Ошибка: {error}</p>;

        // В бонусном режиме показываем только разрешённую категорию
        const bonusCategoryId = bonusPosition?.categoryId ? Number(bonusPosition.categoryId) : null;
        const displayCrystals = isBonusMode && bonusCategoryId
            ? crystals.filter(c => Number(c.id) === bonusCategoryId)
            : crystals;

        return (
            <div className={`flex w-full pb-[16px] px-[16px] pt-[0px] flex-row items-center justify-center gap-[10px] ${isLimited || isBonusMode ? 'opacity-50 pointer-events-none' : ''}`}>
                {[...displayCrystals].reverse().map(item => {
                    const isSelected = selectedId === item.id;
                    return (
                        <div
                            key={item.id}
                            onClick={() => !isLimited && !isBonusMode && dispatch(setSelectedId(item.id))}
                            className={`flex flex-col items-center justify-center w-[92px] h-[70px] gap-[8px] ${isLimited || isBonusMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            <img
                                width={30} height={30}
                                src={icons[item.gemId] || ''}
                                alt={`gem-${item.gemId}`}
                            />
                            <div
                                className={`
                  w-[92px] h-[32px] px-[14px] py-[7px] gap-[6px] text-[14px] rounded-[8px] whitespace-nowrap 
                  flex items-center justify-center
                  ${isSelected ? 'border border-white' : 'bg-white/15 border border-white/10'}
                `}>
                                {item.name}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="w-full h-full flex flex-col overflow-y-auto">
            <PromoTypeIntroBanner
                enabled={!isEditMode}
                variant="onlyCrystals"
                title="Crystals only Promo"
                lines={['The simplest type of promo.']}
            />
            <div className="pt-[24px] pb-[10px]">
                {/* <Header
                    leftButtonText="Close"
                    centerTitle={isEditMode ? "Edit Promo" : isBonusMode ? "Free Promo" : "Create Promo"}
                    onLeftClick={handleClose}
                    useBackIcon={false}
                /> */}
            </div>
            
            {/* Предупреждение при ограниченном режиме */}
            {isEditMode && isLimited && (
                <div className="mx-[15px] mb-2 p-3 bg-[#FFC813]/20 border border-[#FFC813] rounded-lg">
                    <span className="text-[#FFC813] text-[12px]">
                        ⚠️ Promo is paid. You can only edit tasks.
                    </span>
                </div>
            )}
            
            <div className="flex-1 flex flex-col gap-[8px] px-[15px] pt-[20px] pb-[30px]">
            <PromoForm Type={type} isEditMode={isEditMode} isLimited={isLimited} />

            <div className="flex flex-col gap-[8px]">
                <FormAccardion
                    number={1}
                    title="Reward Categories"
                    child={
                        selectedCategory ? (
                            <div className="w-[80px] h-[26px]">
                                <CoinBadge 
                                    fixedWidth={80}
                                    token={icons[selectedCategory.gemId]} 
                                    count={getCrystalCategoryBudget(selectedCategory)} 
                                    iconHeight={22} 
                                    iconWidth={22}
                                />
                            </div>
                        ) : (
                            <div className="w-[80px] h-[26px]">
                                <CoinBadge icon="crown" count={0} iconHeight={22} iconWidth={22}/>
                            </div>
                        )
                    }
                    content={<Categories/>}
                    disabled={isLimited}
                />
                {/* <FormAccardion
                    number={2}
                    title="AddPromoter"
                    child={<div className={`w-[110px] h-[34px] rounded-[8px] ${isLimited ? 'bg-[#959595]' : 'bg-[#D3D3D3]'} flex flex-row items-center justify-center whitespace-nowrap`}>
                        <span className='text-[16px] text-black font-roboto font-semi'>Complete</span>
                    </div>}
                    disabled={isLimited}
                /> */}
                <FormAccardion
                    number={2}
                    title="Add tasks"
                    child={<div className='w-[44px] h-[26px] rounded-[40px] bg-[#D3D3D3] flex flex-row items-center justify-center whitespace-nowrap'>
                        <span className='text-[13px] text-black font-roboto font-medium'>{tasksCount}/{maxTasks}</span>
                    </div>}
                    content={<TasksList maxTasks={maxTasks} />}
                />
            </div>

            {/* Кнопка для бонусного режима */}
            {isBonusMode && (
                <div className='w-full flex flex-row items-center justify-center mt-4 mb-4'>
                    <div
                        onClick={isFormComplete ? createNewPromo : undefined}
                        className={`rounded-[8px] flex flex-row items-center justify-center gap-2 w-[280px] h-[48px] cursor-pointer transition-all duration-200 ${
                            isFormComplete 
                                ? 'opacity-100' 
                                : 'opacity-50 cursor-not-allowed'
                        }`}
                        style={{ background: 'linear-gradient(90deg, #DC1FFF 0%, #3A88F7 100%)' }}
                    >
                        <span className="text-white text-[15px]" style={{ fontFamily: 'Anton, sans-serif' }}>
                            1st Time Free
                        </span>
                        <span className="text-white/70 text-[11px]">
                            · {bonusPosition?.maxParticipants || 0} max · {bonusPosition?.maxTasks || 0} tasks
                        </span>
                    </div>
                </div>
            )}

            {/* FINISH/UPDATE — inactive until form complete + ≥1 task; tap shows hint snackbar */}
            {!isBonusMode && (
                <div className='w-full flex flex-row items-center justify-center mt-4 mb-4'>
                    <div
                        role="button"
                        tabIndex={0}
                        aria-disabled={!canSubmitPromo}
                        onClick={handleFinishClick}
                        className={`rounded-[6px] flex flex-row items-center justify-center w-[244px] h-[45px] font-bold text-[15px] transition-colors duration-200 ${
                            canSubmitPromo
                                ? `cursor-pointer ${
                                    isEditMode
                                        ? 'bg-[#F59E0B] hover:bg-[#D97706] text-black'
                                        : 'bg-[#207FE0]/90 hover:bg-[#1a6bc7] text-white'
                                }`
                                : 'bg-[#353232] text-[#959595] cursor-pointer opacity-60'
                        }`}
                    >
                        {isEditMode ? 'UPDATE' : 'FINISH'}
                    </div>
                </div>
            )}

            {/* Snackbar для уведомлений */}
            <Snackbar
                message={snackbar.message}
                type={snackbar.type}
                isVisible={snackbar.isVisible}
                isAnimated={snackbarAnimation}
                tokenData={snackbar.tokenData}
                blockchainName={snackbar.blockchainName}
                isBlockchainSwitched={snackbar.isBlockchainSwitched}
                onClose={closeSnackbar}
            />
            </div>
        </div>
    );
}
